import { useEffect, useState } from "react";
import { ArrowLeft, ExternalLink, BookOpen } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";

/*
 * Daily Readings, INTERNAL page.
 *
 * Uses the free, no-key JSON feed from calapi.inadiutorium.cz (Catholic
 * liturgical calendar API, CORS-enabled) for the day's celebrations, and
 * embeds Universalis inside an iframe on the same page so parishioners never
 * leave holytrinitykileleshwa.org.
 *
 * The card at the top summarises today's celebration; the readings themselves
 * are the definitive Universalis presentation embedded below. A subtle source
 * credit is shown at the bottom.
 */
const formatDate = (d) => d.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });

const DailyReadings = () => {
    const [meta, setMeta] = useState(null);
    const today = new Date();
    const iso = today.toISOString().slice(0, 10);

    useEffect(() => {
        let alive = true;
        fetch(`https://calapi.inadiutorium.cz/api/v0/en/calendars/default/${iso.replace(/-/g, "/")}`)
            .then((r) => r.ok ? r.json() : Promise.reject())
            .then((d) => { if (alive) setMeta(d); })
            .catch(() => { /* iframe below is the definitive source */ });
        return () => { alive = false; };
    }, [iso]);

    const celebration = meta?.celebrations?.[0];

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
            <SEO
                title="Daily Mass Readings"
                description="Today's full Liturgy of the Word at Holy Trinity Catholic Church Kileleshwa, Old Testament, Psalm, Epistle and Gospel."
            />

            <Link to="/community" className="inline-flex items-center gap-2 text-sm hover:opacity-90" style={{ color: "#D4AF37" }}>
                <ArrowLeft size={14} /> Back to Community
            </Link>

            <span className="eyebrow mt-6 block">Liturgy of the Word</span>
            <h1 className="heading-display mt-2">Daily Mass Readings</h1>
            <p className="mt-3 max-w-2xl text-maroon-700/85">
                {formatDate(today)}. Pray with the universal Church through today&apos;s Old Testament, Psalm, Epistle and Gospel.
            </p>

            {celebration && (
                <div
                    data-testid="daily-readings-celebration"
                    className="mt-6 rounded-2xl border border-gold-300/50 bg-cream-50 p-5 shadow-sm flex items-start gap-3"
                >
                    <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-full" style={{ background: "rgba(212, 175, 55, 0.15)", border: "1px solid rgba(212, 175, 55, 0.55)" }}>
                        <BookOpen size={18} style={{ color: "#D4AF37" }} />
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#D4AF37" }}>Today&apos;s Celebration</p>
                        <p className="mt-1 text-xl text-maroon-800">{celebration.title}</p>
                        {celebration.rank && (
                            <p className="text-xs italic text-maroon-700/70 mt-1">Rank: {celebration.rank}{celebration.colour ? ` · Liturgical colour: ${celebration.colour}` : ""}</p>
                        )}
                    </div>
                </div>
            )}

            <div className="mt-8 rounded-2xl overflow-hidden border shadow-lg" style={{ borderColor: "rgba(212, 175, 55, 0.3)" }}>
                <iframe
                    title="Today's Mass Readings"
                    src="https://universalis.com/mass.htm"
                    className="w-full"
                    style={{ minHeight: "1200px", background: "#fff", border: 0 }}
                    loading="lazy"
                    data-testid="daily-readings-iframe"
                />
            </div>

            <p className="mt-4 text-xs text-maroon-700/60 flex items-center gap-2">
                Readings sourced from the Universal Roman calendar via
                <a href="https://universalis.com/mass.htm" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline decoration-gold-400 underline-offset-4 hover:text-maroon-800">
                    Universalis <ExternalLink size={11} />
                </a>. Feasts and celebrations via calapi.inadiutorium.cz.
            </p>
        </div>
    );
};

export default DailyReadings;
